import { useEffect, useState } from "react";
import { Save, ShieldCheck, KeyRound, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { useAppConfig } from "@/context/ConfigContext";
import { PageHeader } from "@/pages/Dashboard";
import { saveConfig } from "@/lib/api";
import { toast } from "sonner";

export default function Settings() {
  const { config, setConfig, refreshConfig, refreshStatus, refreshModels } = useAppConfig();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({
        base_url: config.base_url,
        timeout: config.timeout,
        default_model: config.default_model,
        default_temperature: config.default_temperature,
        default_max_tokens: config.default_max_tokens,
      });
    }
  }, [config]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const data = await saveConfig({
        base_url: form.base_url,
        timeout: Number(form.timeout),
        default_model: form.default_model,
        default_temperature: Number(form.default_temperature),
        default_max_tokens: Number(form.default_max_tokens),
      });
      if (data.ok) {
        setConfig(data.config);
        await refreshConfig();
        await refreshStatus();
        await refreshModels();
        toast.success("Configuración guardada");
      } else {
        toast.error(data.error || "No se pudo guardar la configuración");
      }
    } catch (e) {
      toast.error(e?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <div className="krai-enter max-w-3xl">
      <PageHeader
        title="Configuración"
        subtitle="Ajustes del motor local · los valores se guardan de forma persistente"
        action={
          <Button data-testid="save-config-button" onClick={save} disabled={saving} className="rounded-sm bg-white text-black hover:bg-neutral-200">
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        }
      />

      <div className="space-y-4">
        <section className="rounded-md border border-[#262626] bg-[#121212] p-5">
          <div className="mb-4 flex items-center gap-2 text-[#737373]">
            <Link2 className="h-4 w-4" />
            <span className="font-mono text-[11px] uppercase tracking-widest">Conexión</span>
          </div>

          <Field label="Local AI Base URL" hint="Endpoint OpenAI-compatible (llama-server / llama.cpp)">
            <Input
              data-testid="config-base-url"
              value={form.base_url}
              onChange={(e) => update("base_url", e.target.value)}
              className="border-[#262626] bg-[#0a0a0a] font-mono text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
            />
          </Field>

          <Field label="Timeout (segundos)">
            <Input
              data-testid="config-timeout"
              type="number"
              min={1}
              value={form.timeout}
              onChange={(e) => update("timeout", e.target.value)}
              className="border-[#262626] bg-[#0a0a0a] font-mono text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
            />
          </Field>
        </section>

        <section className="rounded-md border border-[#262626] bg-[#121212] p-5">
          <div className="mb-4 flex items-center gap-2 text-[#737373]">
            <span className="font-mono text-[11px] uppercase tracking-widest">Valores por defecto</span>
          </div>

          <Field label="Modelo por defecto" hint="ID de un modelo detectado (opcional)">
            <Input
              data-testid="config-default-model"
              value={form.default_model}
              onChange={(e) => update("default_model", e.target.value)}
              placeholder="p. ej. gemma-3"
              className="border-[#262626] bg-[#0a0a0a] font-mono text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
            />
          </Field>

          <Field label={`Temperatura por defecto · ${Number(form.default_temperature).toFixed(2)}`}>
            <Slider
              data-testid="config-default-temperature"
              value={[Number(form.default_temperature)]}
              min={0}
              max={2}
              step={0.05}
              onValueChange={(v) => update("default_temperature", v[0])}
            />
          </Field>

          <Field label="Max tokens por defecto">
            <Input
              data-testid="config-default-max-tokens"
              type="number"
              min={1}
              value={form.default_max_tokens}
              onChange={(e) => update("default_max_tokens", e.target.value)}
              className="border-[#262626] bg-[#0a0a0a] font-mono text-sm focus-visible:ring-1 focus-visible:ring-neutral-500"
            />
          </Field>
        </section>

        <section className="rounded-md border border-[#262626] bg-[#121212] p-5">
          <div className="mb-4 flex items-center gap-2 text-[#737373]">
            <KeyRound className="h-4 w-4" />
            <span className="font-mono text-[11px] uppercase tracking-widest">Seguridad</span>
          </div>

          <div className="flex items-center justify-between rounded-sm border border-[#262626] bg-[#0a0a0a] px-3 py-2.5">
            <span className="text-sm text-[#a3a3a3]">LOCAL_AI_API_KEY</span>
            <span
              data-testid="config-apikey-status"
              className={`font-mono text-xs ${config?.api_key_configured ? "text-emerald-400" : "text-[#737373]"}`}
            >
              {config?.api_key_configured ? "configurada (env)" : "vacía (no requerida)"}
            </span>
          </div>
          <p className="mt-2 text-xs text-[#737373]">
            La API key se define solo mediante variable de entorno en el backend y nunca se expone al navegador.
          </p>

          <div className="mt-4 flex items-start gap-2 rounded-sm border border-[#262626] bg-[#0a0a0a] px-3 py-2.5">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
            <div>
              <div className="text-sm text-[#d4d4d4]">Anti-SSRF · lista blanca de hosts</div>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="config-allowed-hosts">
                {(config?.allowed_hosts || []).map((h) => (
                  <span key={h} className="rounded-sm border border-[#404040] px-2 py-0.5 font-mono text-[11px] text-[#a3a3a3]">
                    {h}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-4 space-y-1.5 last:mb-0">
      <label className="font-mono text-xs uppercase tracking-wider text-[#a3a3a3]">{label}</label>
      {children}
      {hint && <p className="text-xs text-[#737373]">{hint}</p>}
    </div>
  );
}
